import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, GitBranch, Shield, Users, FileText } from 'lucide-react';
import { ContactForm } from '../components/ContactForm';
import { SEO } from '../components/SEO';

export const PDMLink = () => {
  const features = [
    {
      icon: <GitBranch className="w-10 h-10 text-[#00890b]" />,
      title: 'Change Management',
      description: 'Comprehensive change request, change notice, and change activity management',
    },
    {
      icon: <Shield className="w-10 h-10 text-[#00890b]" />,
      title: 'Configuration Management',
      description: 'Product structure management, variant configuration, and effectivity control',
    },
    {
      icon: <FileText className="w-10 h-10 text-[#00890b]" />,
      title: 'Document Management',
      description: 'Centralized document control with version management and access control',
    },
    {
      icon: <Users className="w-10 h-10 text-[#00890b]" />,
      title: 'Collaboration',
      description: 'Team collaboration tools for distributed engineering organizations',
    },
  ];

  const capabilities = [
    'Change Request Management',
    'Change Notice Processing',
    'Problem Report Tracking',
    'Variance & Deviation Management',
    'Product Structure Management',
    'Part Classification & Reuse',
    'BOM Management',
    'Baseline Management',
    'As-Designed vs As-Manufactured',
    'Release Management',
    'Promotion Workflows',
    'Access Control & Security',
  ];

  const benefits = [
    'Reduce change cycle time by up to 50%',
    'Improve collaboration across global teams',
    'Ensure regulatory compliance',
    'Minimize errors in product configuration',
    'Accelerate time-to-market',
    'Enhance traceability and auditability',
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Hire Windchill PDMLink Customization Expert | Outsource PDMLink Development"
        description="Hire expert Windchill PDMLink developers for Change Management and Configuration Management. Outsource your PDMLink customization to experienced professionals with 15+ years expertise."
        keywords="hire windchill pdmlink expert, outsource pdmlink customization, hire pdmlink developer, windchill change management expert, hire pdmlink consultant, outsource windchill pdmlink"
        canonical="/hire-pdmlink-customization-expert"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-white via-green-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="mb-6 bg-[#00890b] hover:bg-[#006a08] text-white px-4 py-2">
              Windchill PDMLink Experts
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Hire Windchill PDMLink Customization Expert
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Outsource your PDMLink development to expert consultants. Hire experienced
              Windchill PDMLink specialists for Change Management and Configuration Management
            </p>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Windchill PDMLink Expertise</h2>
            <p className="text-lg text-gray-600 mb-4">
              Windchill PDMLink is the industry-leading Product Data Management solution that helps
              organizations manage product information throughout the entire lifecycle. Our 15+ years
              of experience enables us to deliver tailored PDMLink solutions that drive efficiency and
              innovation.
            </p>
            <p className="text-lg text-gray-600">
              We specialize in both Change Management and Configuration Management modules, providing
              end-to-end support from initial implementation to ongoing optimization and upgrades.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Key Features</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-center mb-4">{feature.icon}</div>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                  <CardDescription>{feature.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our PDMLink Capabilities</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {capabilities.map((capability, index) => (
              <div
                key={index}
                className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors"
              >
                <CheckCircle className="w-6 h-6 text-[#00890b] flex-shrink-0" />
                <span className="text-gray-700 font-medium">{capability}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Business Benefits</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-[#00890b] flex-shrink-0 mt-1" />
                    <p className="text-gray-700 font-medium">{benefit}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Why Choose Our Windchill PDMLink Services?
            </h2>
            <p className="text-lg text-gray-600 mb-4">
              Our team of certified Windchill developers brings 15+ years of hands-on experience in
              Windchill PDMLink implementations, customizations, and migrations. We understand the
              complexities of enterprise PLM environments and deliver solutions that scale with your
              business.
            </p>
            <p className="text-lg text-gray-600">
              Whether you need help with change management workflows, configuration management
              optimization, or custom PDMLink development, our experts are ready to assist. We follow
              industry best practices and PTC guidelines to ensure your Windchill environment is
              robust, secure, and optimized for performance.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <ContactForm />
    </div>
  );
};